<template>
  <section v-if="visible" class="flight-bookings-list" :aria-busy="loading" :aria-label="title || 'Flight bookings'">
    <app-cells v-if="title"><h2 class="title">{{ title }}</h2></app-cells>
    <div v-if="loading" class="flight-bookings__cards" role="status" aria-label="Loading flight bookings">
      <app-card v-for="index in 3" :key="index" design="new" padding="compact" aria-hidden="true">
        <div class="flight-bookings__skeleton-row"><span /><span /></div>
        <div class="flight-bookings__skeleton-row"><span /><span /></div>
        <div class="flight-bookings__skeleton-row"><span /><span /></div>
      </app-card>
    </div>
    <div v-else-if="loadError" class="flight-bookings__error" role="alert">
      <app-no-results class="flight-bookings__empty" design="new" title="Unable to load bookings" :text="loadError" />
      <app-button theme="dark" @click.native="loadBookings(pagination.page)">Retry</app-button>
    </div>
    <template v-else-if="bookings.length">
      <div class="flight-bookings__cards">
        <flight-booking-card v-for="booking in bookings" :key="booking.id" :booking="booking" />
      </div>
      <app-cells position="between" :indent="false" margin-top="20px">
        <nav :aria-label="title ? title + ' pages' : 'Flight booking pages'">
          <paginate
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :click-handler="loadBookings"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            container-class="pagination"
            page-class="pagination__item"
            page-link-class="pagination__link"
            prev-class="pagination__item pagination__item--prev"
            next-class="pagination__item pagination__item--next"
            next-link-class="pagination__link pagination__link--next"
            prev-link-class="pagination__link pagination__link--prev"
          />
        </nav>
        <app-table-records :all="pagination.count" :count="bookings.length" :page="pagination.page" :limit="pageSize" />
      </app-cells>
    </template>
    <app-no-results v-else class="flight-bookings__empty" design="new" title="No flight bookings" text="There’s nothing to display at the moment." />
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { getFlightsBookings } from '@/http'
import FlightBookingCard from '@/components/flights/FlightBookingCard.vue'
import { responseError, rows } from './booking-detail'

export default {
  name: 'FlightBookingsList',
  components: { FlightBookingCard },
  props: {
    filters: { type: Object, default: () => ({}) },
    title: { type: String, default: '' },
    hideEmpty: { type: Boolean, default: false },
    pageSize: { type: Number, default: 10 }
  },
  data() {
    return {
      bookings: [],
      loading: true,
      requestId: 0,
      loadError: '',
      pagination: {
        page: 1,
        count: 0,
        pages: 1
      }
    }
  },
  computed: {
    visible() {
      return !this.hideEmpty || this.loading || Boolean(this.loadError) || this.bookings.length > 0
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(filters, previous) {
        if (JSON.stringify(filters) === JSON.stringify(previous)) return
        // Invalidate immediately, including the debounce window before the next search.
        this.requestId++
        this.loading = true
        this.loadError = ''
        this.pagination.page = 1
        this.searchBookings.cancel()
        if (filters.search !== previous.search && filters.airline === previous.airline && filters.booking_mode === previous.booking_mode) {
          this.searchBookings()
        } else {
          this.loadBookings()
        }
      }
    }
  },
  created() {
    this.searchBookings = debounce(() => this.loadBookings(), 350)
    this.loadBookings()
  },
  beforeDestroy() {
    this.requestId++
    this.searchBookings.cancel()
  },
  methods: {
    async loadBookings(page = 1) {
      this.searchBookings.cancel()
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = ''
      this.pagination.page = page

      try {
        const { data } = await getFlightsBookings({ ...this.filters, page, page_size: this.pageSize }, { localErrors: true })
        if (requestId !== this.requestId) return
        this.bookings = rows(data)
        this.pagination.count = data.count === undefined ? this.bookings.length : data.count
        this.pagination.pages = data.pages || Math.max(1, Math.ceil(this.pagination.count / this.pageSize))
      } catch (error) {
        if (requestId === this.requestId) this.loadError = responseError(error, 'Unable to load bookings')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.flight-bookings-list + .flight-bookings-list
  margin-top: 32px

.flight-bookings__cards
  display: grid
  gap: 12px

.flight-bookings__empty
  font-family: $font-family-base

.flight-bookings__error
  display: flex
  align-items: center
  flex-direction: column
  gap: $ui-space-16

.flight-bookings__skeleton-row
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: $ui-space-16
  padding: $ui-space-12 0

  & + &
    border-top: 1px solid $ui-line

  span
    display: block
    height: 32px
    border-radius: $ui-control-radius
    background: $ui-line

@media (max-width: 760px)
  .flight-bookings__skeleton-row
    grid-template-columns: minmax(0, 1fr)
</style>
